/**
 * The (i) and the paragraphs behind it — what a figure, a column, a chart or a control really is
 * (audit v5b D-01; DEC-110/DEC-111, `docs/adr/9046` and `docs/adr/9047`).
 *
 * One implementation for every (i) in the product: `Panel`'s and `ScopeChooser`'s (through
 * `@/planning`'s re-export), a `DataTable` column header's, a `Measure`'s, a template KPI's and a
 * chart's. It lives here, among the primitives, because `Measure` is a primitive and cannot reach
 * up into `src/planning/`.
 *
 * - The trigger is a real `<button>` named «Informazioni su {title}» (`panel.about`), reached by
 *   Tab and opened with Enter or Space; the popover is Radix's, so Escape closes it and focus
 *   returns to the button.
 * - Every paragraph is a catalogue key; `values` fills its placeholders with the figures the
 *   screen read (a run, an as-of date, a threshold). Nothing is written here.
 * - `size="sm"` is the one a table header and a KPI label carry: the icon at the label's height,
 *   so the header row does not grow.
 */

import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixPopover from '@radix-ui/react-popover';
import { Info } from 'lucide-react';

import { FOCUS_RING } from './focus';

/**
 * Rewrites the figures an (i) text names before they fill its placeholders — a HOOK, called by the
 * paragraphs only while the popover is open, so a read it needs (the run directory) is made when
 * a planner asks, not when the page is drawn. The app mounts one (`RunWordsAboutProvider`,
 * `@/planning`) so a run named in a help text reads as words — «CO · 2026-W41 · Negozi diretti ·
 * eseguita il 9 ott 2026» — and never as an identifier (audit A1, 2026-10-10). With none mounted
 * the values are used as given.
 */
export type UseAboutValues = (values: Readonly<Record<string, string | number>>) => Readonly<Record<string, string | number>>;

const asGiven: UseAboutValues = (values) => values;

export const AboutValuesContext = createContext<UseAboutValues>(asGiven);

export interface AboutPopoverProps {
  /** One paragraph per key, in order. */
  readonly aboutKeys: readonly string[];
  /** What the (i) explains, already translated — it names the button for a screen reader. */
  readonly title: string;
  readonly values?: Readonly<Record<string, string | number>>;
  /** `sm`: inside a table header or a KPI label. Default `md`, beside a section title. */
  readonly size?: 'md' | 'sm';
}

export function AboutPopover({ aboutKeys, title, values, size = 'md' }: AboutPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const aboutLabel = useMemo(() => t('panel.about', { title }), [t, title]);
  const small = size === 'sm';
  return (
    <RadixPopover.Root>
      <RadixPopover.Trigger asChild>
        <button
          type="button"
          title={aboutLabel}
          className={`text-ink-secondary hover:text-ink-primary inline-flex shrink-0 items-center justify-center rounded-full ${small ? 'size-4' : 'size-6'} ${FOCUS_RING}`}
        >
          <Info size={small ? 12 : 16} aria-hidden />
          <span className="sr-only">{aboutLabel}</span>
        </button>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="start"
          sideOffset={4}
          aria-label={aboutLabel}
          className="bg-surface-raised border-popover-border shadow-popover z-popover flex max-h-[var(--radix-popover-content-available-height)] max-w-md flex-col gap-2 overflow-y-auto rounded-control border p-3 text-start font-normal normal-case tracking-normal"
        >
          <AboutParagraphs aboutKeys={aboutKeys} values={values} />
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/** The paragraphs — rendered only while the popover is open. */
function AboutParagraphs({ aboutKeys, values }: {
  readonly aboutKeys: readonly string[];
  readonly values: AboutPopoverProps['values'];
}): React.JSX.Element {
  const { t } = useTranslation();
  const useValues = useContext(AboutValuesContext);
  const shown = useValues(values ?? {});
  return (
    <>
      {aboutKeys.map((key) => (
        <p key={key} className="text-caption text-ink-secondary">{t(key, shown)}</p>
      ))}
    </>
  );
}
